// passiveAssetNormalizers.js
// The IoT Beacon + RFID APIs return raw read records whose exact field names
// vary between deployments. We normalize each record into a stable shape so
// the UI does not need to know about every alias.

const firstDefined = (...values) => {
  for (const v of values) {
    if (v !== null && v !== undefined && v !== '') return v;
  }
  return null;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// RFID reads sometimes carry RSSI as a hex string (e.g. rssiHex: "11" → 17).
// Returns null for anything not parseable as hex.
const hexToDecimalOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const cleaned = String(value).trim().replace(/^0x/i, '');
  if (!/^[0-9a-fA-F]+$/.test(cleaned)) return null;
  const n = parseInt(cleaned, 16);
  return Number.isFinite(n) ? n : null;
};

// Parses "lat,long" / "lat;long" / "lat long" pairs into [lat, lng].
// Returns [null, null] for empty / malformed input.
const splitLatLong = (value) => {
  if (value === null || value === undefined || value === '') return [null, null];
  const parts = String(value).split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return [null, null];
  return [toNumberOrNull(parts[0]), toNumberOrNull(parts[1])];
};

const resolveLatLng = (record) => {
  const direct = [
    toNumberOrNull(firstDefined(record.latitude, record.lat)),
    toNumberOrNull(firstDefined(record.longitude, record.lng, record.lon))
  ];
  if (direct[0] !== null && direct[1] !== null) return direct;

  const combined = firstDefined(
    record.lat_long,
    record.latLong,
    record.latlong,
    record.latlng,
    record.latLng,
    record.location_coords,
    record.coordinates,
    record.coords
  );
  if (combined && typeof combined === 'object' && !Array.isArray(combined)) {
    return [
      toNumberOrNull(firstDefined(combined.lat, combined.latitude)),
      toNumberOrNull(firstDefined(combined.lng, combined.lon, combined.longitude))
    ];
  }
  if (Array.isArray(combined) && combined.length >= 2) {
    return [toNumberOrNull(combined[0]), toNumberOrNull(combined[1])];
  }
  return splitLatLong(combined);
};

const toMillis = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export function normalizeRfidRead(record) {
  if (!record || typeof record !== 'object') return null;

  const assetIdentifier = firstDefined(
    record.rfid,
    record.instance_id,
    record.instanceId,
    record.tagId,
    record.tag_id
  );

  const readerImei = firstDefined(
    record.imei,
    record.device_id,
    record.deviceId,
    record.readerImei,
    record.reader_imei
  );

  const eventTime = toMillis(firstDefined(
    record.event_time,
    record.eventTime,
    record.timestamp,
    record.ts,
    record.created_at,
    record.createdAt
  ));

  const receivedTime = toMillis(firstDefined(
    record.received_time,
    record.receivedTime,
    record.server_time,
    record.serverTime,
    record.createdAt
  ));

  const rfidLatLng = resolveLatLng(record);

  return {
    id: firstDefined(record.id, record._id, `${assetIdentifier || 'rfid'}-${eventTime || Math.random()}`),
    sensorType: 'RFID',
    assetIdentifier: assetIdentifier || '-',
    readerImei: readerImei || '-',
    eventTime,
    receivedTime,
    status: firstDefined(record.status, record.state) || '-',
    rssi: firstDefined(
      toNumberOrNull(firstDefined(record.rssi, record.RSSI, record.signal)),
      hexToDecimalOrNull(firstDefined(record.rssiHex, record.rssi_hex, record.RSSIHex))
    ),
    latitude: rfidLatLng[0],
    longitude: rfidLatLng[1],
    zone: firstDefined(record.zone, record.zoneName, record.location, record.area) || '-',
    raw: record
  };
}

export function normalizeBeaconRead(record) {
  if (!record || typeof record !== 'object') return null;

  const assetIdentifier = firstDefined(
    record.mac,
    record.beaconMac,
    record.beacon_mac,
    record.instance_id,
    record.instanceId
  );

  const readerImei = firstDefined(
    record.imei,
    record.device_id,
    record.deviceId,
    record.detected_by,
    record.detectedBy,
    record.reader_imei
  );

  const eventTime = toMillis(firstDefined(
    record.event_time,
    record.eventTime,
    record.timestamp,
    record.ts,
    record.created_at,
    record.createdAt
  ));

  const receivedTime = toMillis(firstDefined(
    record.received_time,
    record.receivedTime,
    record.server_time,
    record.serverTime,
    record.createdAt
  ));

  const beaconLatLng = resolveLatLng(record);

  return {
    id: firstDefined(record.id, record._id, `${assetIdentifier || 'beacon'}-${eventTime || Math.random()}`),
    sensorType: 'BEACON',
    assetIdentifier: assetIdentifier || '-',
    readerImei: readerImei || '-',
    eventTime,
    receivedTime,
    status: firstDefined(record.status, record.state) || '-',
    rssi: firstDefined(
      toNumberOrNull(firstDefined(record.rssi, record.RSSI, record.signal)),
      hexToDecimalOrNull(firstDefined(record.rssiHex, record.rssi_hex, record.RSSIHex))
    ),
    latitude: beaconLatLng[0],
    longitude: beaconLatLng[1],
    zone: firstDefined(record.zone, record.zoneName, record.location, record.area) || '-',
    raw: record
  };
}

export function normalizePassiveRead(record, sensorType) {
  if (sensorType === 'RFID') return normalizeRfidRead(record);
  if (sensorType === 'BEACON') return normalizeBeaconRead(record);
  return null;
}

// Keys we prefer (in order) when several arrays are present in the payload.
const PREFERRED_ARRAY_KEYS = [
  'data', 'rows', 'results', 'result', 'records', 'items', 'list',
  'reads', 'docs', 'content', 'payload'
];

const isArrayOfObjects = (arr) =>
  Array.isArray(arr) && (arr.length === 0 || typeof arr[0] === 'object');

// Accepts a raw API response payload in almost any shape (array,
// { data: [...] }, { data: { rows: [...] } }, { result: { items: [...] } },
// etc.) and returns a plain array of record objects. Falls back to a bounded
// recursive search so unexpected wrapper keys still yield rows.
export function extractRecords(payload, depth = 0) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];

  // 1) Try the well-known keys first (shallow), in priority order.
  for (const key of PREFERRED_ARRAY_KEYS) {
    if (isArrayOfObjects(payload[key])) return payload[key];
  }

  // 2) Then recurse into the well-known object wrappers.
  if (depth < 4) {
    for (const key of PREFERRED_ARRAY_KEYS) {
      const child = payload[key];
      if (child && typeof child === 'object' && !Array.isArray(child)) {
        const found = extractRecords(child, depth + 1);
        if (found.length) return found;
      }
    }

    // 3) Last resort: scan every value for the first array of objects.
    for (const key of Object.keys(payload)) {
      const value = payload[key];
      if (isArrayOfObjects(value) && value.length) return value;
    }
  }

  return [];
}

export function normalizeRfidList(payload) {
  return extractRecords(payload)
    .map(normalizeRfidRead)
    .filter(Boolean);
}

export function normalizeBeaconList(payload) {
  return extractRecords(payload)
    .map(normalizeBeaconRead)
    .filter(Boolean);
}
